import React, { useState } from "react";
// Components
import Loading from "../loading/loading";

// Styles
import Styles from "./button.module.scss";

// Constants
import { COMPRESSED, LARGE, MEDIUM } from "../buttonSize";

// Size variants
export const sizes = {
  small: { width: "200px", height: "35px" },
  large: { width: "300px", height: "45px" },
  medium: { width: "150px", height: "40px" },
  compressed: {
    minWidth: "110px",
    width: "100%",
    height: "30px",
    fontSize: "12px",
  },
};

// Style constants from your configuration
export const BACKGROUNDCOLOR = "#f5f5f5";
export const LINKHOVERBACKGROUND = "#e2f3ff";
export const AUTHSIDEBARGRADIENT = "linear-gradient(to top, #202020, #6a6a6a)";
export const LINKCOLOR = "#202020";
export const WHITE = "#fff";
export const PRIMARYCOLOR = "#202020";
export const SECONDARYCOLOR = "#747474";
export const ACCENTCOLOR = "#232323";
export const SUCCESS = "#0F9D58";
export const DANGER = "#D64747";
export const WARNING = "#f4a63b";
export const MAINGREEN = "#21ff91";
export const SUCCESSDARKCOLOR = "#008B47";
export const DANGERDARKCOLOR = "#B6393A";
export const BORDERCOLOR = "rgba(112, 112, 112, 0.2)";
export const DISABLEDCOLOR = "#E3E3E3";
export const DISABLEDTEXTCOLOR = "#8E8E8E";
export const GRAY = "#d1d5db";
export const DISABLEDCOLORDARK = "#969696";
export const BLACKCOLOR = "#000000";
export const BLUEBACKGROUNDCOLOR = "#3272EC";

// All button styles from your configuration
export const buttonStyles = {
  primary: {
    normal: {
      borderRadius: "8px",
      backgroundColor: ACCENTCOLOR,
      color: MAINGREEN,
      border: 0,
    },
    hover: {
      borderRadius: "8px",
      backgroundColor: ACCENTCOLOR,
      color: WHITE,
      border: 0,
    },
  },
  secondary: {
    normal: {
      borderRadius: "8px",
      backgroundColor: MAINGREEN,
      color: ACCENTCOLOR,
      border: 0,
    },
    hover: {
      borderRadius: "8px",
      backgroundColor: ACCENTCOLOR,
      color: MAINGREEN,
      border: 0,
    },
  },
  submit: {
    normal: {
      borderRadius: "8px",
      backgroundColor: ACCENTCOLOR,
      color: MAINGREEN,
      border: 0,
    },
    hover: {
      borderRadius: "8px",
      backgroundColor: MAINGREEN,
      color: ACCENTCOLOR,
      border: 0,
    },
  },
  borderDark: {
    normal: {
      borderRadius: "8px",
      color: ACCENTCOLOR,
      background: "transparent",
      border: `2px solid ${ACCENTCOLOR}`,
    },
    hover: {
      borderRadius: "8px",
      background: ACCENTCOLOR,
      color: "#21FF91",
      border: `2px solid ${ACCENTCOLOR}`,
    },
  },
  borderLight: {
    normal: {
      borderRadius: "8px",
      color: ACCENTCOLOR,
      background: "transparent",
      border: `2px solid ${GRAY}`,
    },
    hover: {
      borderRadius: "8px",
      background: ACCENTCOLOR,
      color: "#21FF91",
      border: `2px solid ${ACCENTCOLOR}`,
    },
  },

  cancel: {
    normal: {
      borderRadius: "8px",
      boxShadow: "none",
      color: "#16B265",
      background: "#D5FFEA",
      border: `2px solid ${"#D5FFEA"}`,
    },
    hover: {
      borderRadius: "8px",
      boxShadow: "none",
      color: "#16B265",
      background: "#D5FFEA",
      border: `2px solid ${"#D5FFEA"}`,
    },
  },
  text: {
    normal: {
      backgroundColor: "transparent",
      color: ACCENTCOLOR,
      border: "none",
    },
    hover: {
      backgroundColor: "transparent",
      color: ACCENTCOLOR,
      textDecoration: "underline",
      border: "none",
    },
  },
  success: {
    normal: {
      borderRadius: "8px",
      backgroundColor: SUCCESS,
      color: WHITE,
      border: 0,
    },
    hover: {
      borderRadius: "8px",
      backgroundColor: SUCCESSDARKCOLOR,
      color: WHITE,
      border: 0,
    },
  },
  danger: {
    normal: {
      borderRadius: "8px",
      backgroundColor: DANGER,
      color: WHITE,
      border: 0,
    },
    hover: {
      borderRadius: "8px",
      backgroundColor: DANGERDARKCOLOR,
      color: WHITE,
      border: 0,
    },
  },
  blueHover: {
    normal: {
      borderRadius: "8px",
      backgroundColor: BLUEBACKGROUNDCOLOR,
      color: WHITE,
      border: 0,
    },
    hover: {
      borderRadius: "8px",
      backgroundColor: "#1a5bd8",
      color: WHITE,
      border: 0,
    },
  },
  disabled: {
    normal: {
      borderRadius: "8px",
      backgroundColor: DISABLEDCOLOR,
      color: DISABLEDTEXTCOLOR,
      cursor: "not-allowed",
      border: `2px solid ${DISABLEDCOLOR}`,
    },
    hover: {
      borderRadius: "8px",
      backgroundColor: DISABLEDCOLOR,
      color: DISABLEDTEXTCOLOR,
      border: `2px solid ${DISABLEDCOLOR}`,
      cursor: "not-allowed",
    },
  },
};

type ButtonType = "submit" | "button" | "reset" | undefined;

// Button component
type StyledButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  style: React.CSSProperties;
  hoverStyle: React.CSSProperties;
  children?: React.ReactNode;
  disabled?: boolean;
  id: string;
  className?: any;
  isLoading?: boolean;
  type?: ButtonType;
};

export const StyledButton: React.FC<StyledButtonProps> = ({
  style,
  hoverStyle,
  children,
  disabled = false,
  className,
  id,
  isLoading,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const currentStyle = isHovered && !disabled ? hoverStyle : style;
  const testId =
    (props as { "data-testid"?: string })["data-testid"] ?? id;

  return (
    <button
      id={id}
      data-testid={testId}
      className={Styles.btnHover + " " + className || ""}
      style={{
        padding: "10px 20px",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.2s ease",
        fontWeight: "500",
        fontSize: "14px",
        textAlign: "center",
        ...currentStyle,
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      {...props}
    >
      {isLoading ? (
        <Loading
        // loadingColor={style.buttonTextColor ?? style.buttonTextColor}
        // width={size === LARGE ? "12px" : size === COMPRESSED ? "8px" : "10px"}
        // height={size === LARGE ? "12px" : size === COMPRESSED ? "8px" : "10px"}
        />
      ) : (
        <p
          className={" mb-0"}
          // style={{
          //   color: buttonStyle.buttonTextColor,
          //   fontSize: size === COMPRESSED ? "12px" : "14px",
          // }}
        >
          {children}
        </p>
      )}
    </button>
  );
};

function Button({
  id,
  isLoading,
  disabled,
  style,
  hoveredStyle,
  size,
  btnTextClass,
  className,
  ...props
}: any) {
  const [buttonStyle, setButtonStyle] = useState(style || {});
  const testId = props["data-testid"] ?? id;

  // Safely get text color from style object
  const getTextColor = (styleObj: any) => {
    if (!styleObj) return ACCENTCOLOR;
    return styleObj.buttonTextColor || styleObj.color || ACCENTCOLOR;
  };

  return (
    <button
      id={id}
      data-testid={testId}
      className={Styles.btn + " " + className || ""}
      style={
        size === COMPRESSED
          ? {
              ...buttonStyle,
              minWidth: "110px",
              width: "100%",
              height: "30px",
            }
          : {
              ...buttonStyle,
              width:
                size === LARGE ? "300px" : size === MEDIUM ? "150px" : "100%",
            }
      }
      disabled={disabled}
      onMouseOver={
        !disabled ? () => setButtonStyle(hoveredStyle || style || {}) : () => {}
      }
      onMouseOut={() => setButtonStyle(style || {})}
      {...props}
    >
      {isLoading ? (
        <Loading
          loadingColor={getTextColor(style)}
          width={size === LARGE ? "12px" : size === COMPRESSED ? "8px" : "10px"}
          height={
            size === LARGE ? "12px" : size === COMPRESSED ? "8px" : "10px"
          }
        />
      ) : (
        <p
          className={`${Styles.btnText + " " + (btnTextClass || "")} mb-0`}
          style={{
            color: getTextColor(buttonStyle),
            fontSize: size === COMPRESSED ? "12px" : "14px",
          }}
        >
          {props.children}
        </p>
      )}
    </button>
  );
}

export default Button;
